import React, { createContext, useContext, useRef, useEffect, useState } from 'react';
import { EventsApiContextType, RequestState, CacheEntry } from './EventsApiContextModels';
import { FeedbackType } from '../generic-components/ai-summary-components/FeedbackButtons';
import { v4 as uuidv4 } from 'uuid';
import { logger } from 'src/logger';
import { useAuth } from './AuthContextProvider';
import { LoadingStatus } from './AppContextModels';

const EventsApiContextInitialData: EventsApiContextType = {
  setLatestPageRequestId: () => {},
  setLatestChartRequestId: () => {},
  clearPageRequestId: () => {},
  clearChartRequestId: () => {},
  getLatestPageRequestId: () => undefined,
  getLatestChartRequestId: () => null,
  setPageRequestState: () => {},
  setChartRequestState: () => {},
  cancelPageRequest: () => false,
  cancelChartRequest: () => false,
  getPageRequestState: () => undefined,
  getChartRequestState: () => null,
  getCacheEntry: () => undefined,
  setCacheEntry: () => {},
  updateFeedback: () => {}
};

const EventsApiContextDetails = createContext<EventsApiContextType>(EventsApiContextInitialData);
export const useEventsApiContext = () => useContext(EventsApiContextDetails);

interface EventsApiContextProviderProps {
  children: React.ReactNode;
}

export const EventsApiContextProvider = ({ children }: EventsApiContextProviderProps) => {
  const userAuthDetails = useAuth();

  const pageRequestIds = useRef<Map<string, string>>(new Map());
  const chartRequestId = useRef<string | null>(null);

  // Enhanced request state tracking
  const pageRequestStates = useRef<Map<string, RequestState>>(new Map());
  const chartRequestState = useRef<RequestState | null>(null);

  // Frontend cache storage
  const frontendCache = useRef<Map<string, CacheEntry>>(new Map());

  const setLatestPageRequestId = (pageType: string, requestId: string) => {
    pageRequestIds.current.set(pageType, requestId);
  };

  const setLatestChartRequestId = (requestId: string | null) => {
    chartRequestId.current = requestId;
  };

  const clearPageRequestId = (pageType: string) => {
    pageRequestIds.current.delete(pageType);
  };

  const clearChartRequestId = () => {
    chartRequestId.current = null;
  };

  const getLatestPageRequestId = (pageType: string): string | undefined => {
    return pageRequestIds.current.get(pageType);
  };

  const getLatestChartRequestId = (): string | null => {
    return chartRequestId.current;
  };

  // Enhanced request state management methods
  const setPageRequestState = (pageType: string, requestState: RequestState) => {
    pageRequestStates.current.set(pageType, requestState);
    // Also maintain backward compatibility with existing ID tracking
    pageRequestIds.current.set(pageType, requestState.id);
  };

  const setChartRequestState = (requestState: RequestState | null) => {
    chartRequestState.current = requestState;
    // Also maintain backward compatibility with existing ID tracking
    chartRequestId.current = requestState?.id || null;
  };

  const cancelPageRequest = (pageType: string): boolean => {
    const existingState = pageRequestStates.current.get(pageType);
    if (existingState) {
      try {
        // Close channel if it exists
        if (existingState.channel) {
          existingState.channel.close();
        }
        // Clear timeout if it exists
        if (existingState.timeout) {
          clearTimeout(existingState.timeout);
        }
      } catch (error) {
        console.warn(`Error cleaning up page request resources: ${error}`);
      }

      // Remove from tracking maps
      pageRequestStates.current.delete(pageType);
      pageRequestIds.current.delete(pageType);
      return true;
    }
    return false;
  };

  const cancelChartRequest = (): boolean => {
    const existingState = chartRequestState.current;
    if (existingState) {
      try {
        // Close channel if it exists
        if (existingState.channel) {
          existingState.channel.close();
        }
        // Clear timeout if it exists
        if (existingState.timeout) {
          clearTimeout(existingState.timeout);
        }
      } catch (error) {
        console.warn(`Error cleaning up chart request resources: ${error}`);
      }

      // Remove from tracking
      chartRequestState.current = null;
      chartRequestId.current = null;
      return true;
    }
    return false;
  };

  const getPageRequestState = (pageType: string): RequestState | undefined => {
    return pageRequestStates.current.get(pageType);
  };

  const getChartRequestState = (): RequestState | null => {
    return chartRequestState.current;
  };

  // Frontend cache methods
  const getCacheEntry = (key: string): CacheEntry | undefined => {
    return frontendCache.current.get(key);
  };

  const setCacheEntry = (key: string, entry: CacheEntry) => {
    frontendCache.current.set(key, entry);
  };

  const updateFeedback = (key: string, feedbackType: FeedbackType) => {
    const existingEntry = frontendCache.current.get(key);

    if (existingEntry) {
      // Update existing entry with feedback
      const updatedEntry = { ...existingEntry, feedbackType };
      frontendCache.current.set(key, updatedEntry);
    } else {
      // Create new feedback-only entry
      const feedbackEntry: CacheEntry = { feedbackType };
      frontendCache.current.set(key, feedbackEntry);
    }
  };

  // Cleanup mechanism to prevent memory leaks on component unmount
  useEffect(() => {
    return () => {
      // Clean up all page requests
      Array.from(pageRequestStates.current.keys()).forEach((pageType) => {
        cancelPageRequest(pageType);
      });
      // Clean up chart request
      cancelChartRequest();
    };
  }, []);

  const contextValue: EventsApiContextType = {
    setLatestPageRequestId,
    setLatestChartRequestId,
    clearPageRequestId,
    clearChartRequestId,
    getLatestPageRequestId,
    getLatestChartRequestId,
    setPageRequestState,
    setChartRequestState,
    cancelPageRequest,
    cancelChartRequest,
    getPageRequestState,
    getChartRequestState,
    getCacheEntry,
    setCacheEntry,
    updateFeedback
  };

  return (
    <EventsApiContextDetails.Provider value={contextValue}>
      {userAuthDetails.userAuthDataLoadingStatus === LoadingStatus.Completed}
      {children}
    </EventsApiContextDetails.Provider>
  );
};
